<script setup lang="ts">
import EnglishLessonPage from "~/features/english/components/EnglishLessonPage.vue";

const textbookAlignedPoints = [
  "Yeh one-act play domestic comedy hai jahan Darry aur Lizzie housework aur outdoor work ke bare me argue karte hain aur roles swap kar lete hain.",
  "Play ka humor is baat se paida hota hai ke Darry aur us ka friend Barry simple housework bhi properly manage nahi kar pate, jab ke Lizzie calmly outdoor work complete kar leti hai.",
  "Exam prep me summary, characters, comic incidents, gender-role theme aur message sab important hote hain.",
];

const chapterCoverage = [
  "Role-swap plot",
  "Darry, Lizzie, and Barry",
  "Humor and comic incidents",
  "Gender-role theme",
  "Domestic labor undervaluation",
  "Ending and message",
];

const quickRevisionPoints = [
  "Play domestic comedy aur role-swap idea par based hai.",
  "Darry overconfident hai aur housework me fail hota hai.",
  "Barry comic support de kar chaos aur barha deta hai.",
  "Lizzie practical aur capable character hai.",
  "Message: housework ko easy samajhna ghalat hai.",
];

const readingQuestions = [
  {
    question: "What is the story of The End of the Beginning?",
    answer: "Darry aur Lizzie argue karte hain ke kis ka kaam zyada difficult hai. Dono ek din ke liye roles exchange kar lete hain. Darry housework karne me repeatedly fail hota hai, Barry bhi us ki help karte hue chaos barha deta hai, aur end me Lizzie successful wapas aati hai jab ke ghar total mess ban chuka hota hai.",
  },
  {
    question: "Why is the play called The End of the Beginning?",
    answer: "Title suggest karta hai ke role-swap experiment sirf ek beginning thi, magar us ka end Darry ki failure ke through ek nayi realization ban jata hai. Is sense me end hi real understanding ki beginning ban jata hai.",
  },
  {
    question: "How is Darry presented in the play?",
    answer: "Darry proud, overconfident aur argumentative hai. Woh samajhta hai ke housework easy hai, lekin practical test me us ki weakness aur foolishness saamne aa jati hai.",
  },
  {
    question: "What is the role of Barry in the play?",
    answer: "Barry comic support character hai jo Darry ki tarah clumsy aur impractical hai. Us ki presence se accidents aur misunderstandings aur zyada funny aur chaotic ho jati hain.",
  },
  {
    question: "What does Lizzie represent in the play?",
    answer: "Lizzie practical wisdom, patience aur competence ko represent karti hai. Woh bina drama ke Darry ka kaam bhi successfully karti hai aur indirectly prove kar deti hai ke us ka husband women's work ko underestimate kar raha tha.",
  },
];

const literaryPoints = [
  {
    label: "Humor",
    detail: "Humor broken crockery, scattered things, rope-and-heifer plan, failed exercises aur Darry-Barry ki clumsiness se generate hota hai. Play situational comedy aur farce dono use karta hai.",
  },
  {
    label: "Theme",
    detail: "Major themes me gender roles, male pride, domestic labor ki underestimation, aur practical reality versus empty claims shamil hain.",
  },
  {
    label: "Message",
    detail: "Play ka message yeh hai ke housework ko easy samajhna ghalat hai. It also criticizes male superiority complex through comedy.",
  },
  {
    label: "Ending",
    detail: "Ending comic aur ironic hai. Darry fail hone ke baad bhi full humility show nahi karta, jis se play ka satirical effect aur strong ho jata hai.",
  },
];

const mcqs = [
  {
    question: "What creates most of the humor in the play?",
    options: [
      { label: "a", text: "serious courtroom arguments", verdict: "incorrect", reason: "Play domestic comedy hai, courtroom drama nahi." },
      { label: "b", text: "Darry and Barry's failure to manage housework", verdict: "correct", reason: "Comic effect role-swap ke baad un ki clumsy failures se paida hota hai." },
      { label: "c", text: "a tragic family secret", verdict: "incorrect", reason: "Play ka mode tragic nahi, comic aur satirical hai." },
      { label: "d", text: "Lizzie's fear of outdoor work", verdict: "incorrect", reason: "Lizzie actually outdoor work successfully handle karti hai." },
    ],
  },
  {
    question: "What does Lizzie mainly represent in the play?",
    options: [
      { label: "a", text: "panic and helplessness", verdict: "incorrect", reason: "Lizzie calm aur capable character hai." },
      { label: "b", text: "practical competence and patience", verdict: "correct", reason: "Woh quietly prove karti hai ke domestic aur outdoor work ko sensibly manage kiya ja sakta hai." },
      { label: "c", text: "comic foolishness", verdict: "incorrect", reason: "Comic foolishness mostly Darry aur Barry me nazar aati hai." },
      { label: "d", text: "rejection of all household duties", verdict: "incorrect", reason: "Play usay responsible aur effective show karta hai, rejecting duties nahi." },
    ],
  },
];

const answerBuilding = [
  "Summary me argument, role-swap, comic disasters aur Lizzie ki return ko order me likho.",
  "Character answer me Darry ko overconfident, Barry ko clumsy aur Lizzie ko sensible likhna useful hota hai.",
  "Theme ya humor wale jawab me gender roles aur domestic chaos dono mention karo.",
  "Title explain karte waqt role-swap se paida hui realization ka idea likhna useful hota hai.",
];

usePageSeo({
  title: "The End of the Beginning Notes | 11th Class English Unit 14",
  description: "11th class English Unit 14 play notes with summary, characters, humor, role reversal, theme and explained MCQs.",
  keywords: "the end of the beginning notes, 11th class english unit 14, class 11 play notes, the end of the beginning summary, one act play mcqs",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Play", item: "/11th/english/play/" },
    { name: "The End of the Beginning", item: "/11th/english/play/the-end-of-the-beginning/" },
  ],
});
</script>

<template>
  <EnglishLessonPage
    unit="Unit 14"
    title="The End of the Beginning"
    intro="Yeh page play ke comic structure, characters aur gender-role theme ko exam prep format me explain karta hai."
    :bullet-sections="[
      { title: 'Textbook-Aligned Snapshot', items: textbookAlignedPoints },
      { title: 'Unit 14 Coverage Checklist', items: chapterCoverage, layout: 'grid' },
      { title: 'Quick Revision Points', items: quickRevisionPoints },
      { title: 'Answer-Building Notes', items: answerBuilding }
    ]"
    :text-sections="[
      { title: 'Reading and Explanation Answers', items: readingQuestions.map((item) => ({ title: item.question, description: item.answer })) },
      { title: 'Key Play Points', items: literaryPoints.map((item) => ({ title: item.label, description: item.detail })) }
    ]"
    :mcq-sections="[
      { title: 'MCQs with Option Explanation', items: mcqs }
    ]"
  />
</template>





